import React, {useEffect, useMemo} from "react";
import {useFormContext, Validate, Path} from "react-hook-form";
import {
    ClientReportSubmitClientOfferHourInput,
    ClientReportSubmitInput,
    HourField,
    ReportServiceType,
    requireServiceType,
    shouldShowFirstTreatmentOfferedDate,
    shouldShowTreatmentOffered,
    TreatmentOfferedOption,
    TreatmentOfferedOptionMap
} from "../../domain";
import {
    hourInputValidationRules,
    shouldRenderClientOfferedHours,
    shouldShowHourInputs,
    stopNonNumericInput,
    validateOfferedHour
} from "./view-domains";
import {OfferedHourInput} from "./OfferedHourInput";
import {ModalDatePicker} from "../ModalDatePicker";
import {TreatmentOfferedInput} from "./TreatmentOfferedInput";
import _ from "lodash";
import { ModalTooltip } from "common/component/ModalTooltip";


type ControlledOfferedHourInputProps = {
    field: keyof ClientReportSubmitClientOfferHourInput;
    label: string;
    disabled: boolean;
    required?: boolean;
    isCurrentOffer?: boolean | undefined;
    noMinValidation?: boolean;
    allowZero?: boolean;
    additionalServiceTypeMapping?: Map<ReportServiceType, HourField[]>
    fixedServiceTypes?: ReportServiceType[];
}

export function ControlledOfferedHourInput({
                                               field,
                                               label,
                                               disabled,
                                               isCurrentOffer = false,
                                               noMinValidation = false,
                                               allowZero = false,
                                               required = true,
                                               additionalServiceTypeMapping,
                                               fixedServiceTypes,
                                           }: ControlledOfferedHourInputProps) {
    const {
        formState: {
            errors
        },
        getValues,
        register,
        trigger,
    } = useFormContext<ClientReportSubmitInput>();
    let formServiceTypes = getValues('serviceType') as ReportServiceType[];
    if (fixedServiceTypes &&  _.isEmpty(formServiceTypes)) {
        formServiceTypes = fixedServiceTypes;
    }

    const validate = useMemo(() => {
        if (isCurrentOffer) {
            return _.noop as Validate<number | null>;
        } else {
            return validateOfferedHour(field, label, getValues, noMinValidation, allowZero)
        }
    }, [isCurrentOffer, field, label, getValues, noMinValidation, allowZero]);

    if (!(shouldShowHourInputs(formServiceTypes, [field], additionalServiceTypeMapping))) {
        return null;
    }

    let defaultRules = hourInputValidationRules;
    if (!required) {
        defaultRules = {...defaultRules, ...{required: false}}
    }

    const registered = register(field, {
        ...defaultRules,
        validate: {
            ...defaultRules.validate,
            offeredHourValidation: validate,
        }
    });
    const defaultOnChange = registered.onChange;
    registered.onChange = async (event) => {
        if (await stopNonNumericInput(event)) {
            return;
        }
        await defaultOnChange(event);
        trigger(field as string as Path<ClientReportSubmitInput>);
    };

    return <OfferedHourInput label={label} disabled={disabled} registered={registered} error={errors[field]?.message}/>;
}

type Props = {
    disabled: boolean;
    reportType: string;
    isClientOffer?: boolean | undefined;
    isNew?: boolean | undefined;
    isStaffingPlan?: boolean | undefined;
    isReturnsTransferRequest?: boolean | undefined;
    forceNotRequiredOfferedHours?: boolean | undefined;
    tooltip?: string | undefined;
};

type offerHourFieldNames = {
    paraprofessional: keyof ClientReportSubmitClientOfferHourInput;
    mid: keyof ClientReportSubmitClientOfferHourInput;
    high: keyof ClientReportSubmitClientOfferHourInput;
    group: keyof ClientReportSubmitClientOfferHourInput;
};

const currentHourFieldNames: offerHourFieldNames = {
    paraprofessional: 'currentOfferedParaprofessionalHours',
    mid: 'currentOfferedMidLevelHours',
    high: 'currentOfferedHighLevelHours',
    group: 'currentOfferedGroupCareHours',
};

const clientHourFieldNames: offerHourFieldNames = {
    paraprofessional: 'offeredParaprofessionalHours',
    mid: 'offeredMidLevelHours',
    high: 'offeredHighLevelHours',
    group: 'offeredGroupCareHours',
};

export function ClientOfferInput(
    {
        disabled, reportType,
        isClientOffer = true,
        isNew = false,
        isStaffingPlan = false,
        isReturnsTransferRequest = false,
        forceNotRequiredOfferedHours = false,
        tooltip = undefined
    }: Props) {
    const {
        watch,
        setValue,
        getValues,
    } = useFormContext<ClientReportSubmitInput>();

    const showThisSection = isNew || requireServiceType(reportType);
    const showTreatmentDateInput = !isStaffingPlan && shouldShowFirstTreatmentOfferedDate(reportType) && shouldShowTreatmentOffered(reportType);
    const showOfferedHoursInputs = (isNew || shouldRenderClientOfferedHours(reportType, getValues('serviceType') as ReportServiceType[]));
    const treatmentOffered = watch('treatmentOffered');
    let required = showTreatmentDateInput ? TreatmentOfferedOptionMap.get(treatmentOffered) === TreatmentOfferedOption.YES : isClientOffer;
    const shouldDisabled = disabled || TreatmentOfferedOptionMap.get(treatmentOffered) === TreatmentOfferedOption.NO;

    useEffect(() => {
        if (TreatmentOfferedOptionMap.get(treatmentOffered) === TreatmentOfferedOption.NO) {
            setValue("offeredGroupCareHours", null);
            setValue("offeredParaprofessionalHours", null);
            setValue("offeredMidLevelHours", null);
            setValue("offeredHighLevelHours", null);
            setValue("firstTreatmentOfferedDate", null);
        }
    }, [treatmentOffered, setValue])

    if (!showThisSection || !(showTreatmentDateInput || showOfferedHoursInputs)) {
        return null;
    }

    const hoursFields: offerHourFieldNames = isClientOffer ? clientHourFieldNames : currentHourFieldNames;
    let title = isClientOffer ? 'Updated Offer' : 'Current Offer';
    if (isReturnsTransferRequest) {
        title = 'Offer';
    }
    if (forceNotRequiredOfferedHours) {
        required = false;
    }

    return (
        <div>
            <div className="w-full text-base pb-2">
                <div className="pb-2 flex items-center">
                    <span className="font-bold">
                        {title}:
                    </span>
                    &nbsp;
                    (hours per month)
                    {tooltip && (
                        <ModalTooltip content={tooltip}/>
                    )}
                </div>

                {showTreatmentDateInput &&
                    <TreatmentOfferedInput sending={disabled}/>
                }
                {showTreatmentDateInput &&
                    <div className="w-full">
                        <ModalDatePicker<ClientReportSubmitInput>
                            label="First Treatment Offered Date"
                            disabled={shouldDisabled}
                            field="firstTreatmentOfferedDate"
                            required={required}
                            />
                    </div>
                }

                <div className="w-full flex flex-wrap">
                    {showOfferedHoursInputs &&
                        <>
                            <ControlledOfferedHourInput field={hoursFields.paraprofessional}
                                                        label="Paraprofessional Hours"
                                                        disabled={shouldDisabled}
                                                        noMinValidation={isReturnsTransferRequest}
                                                        allowZero={isReturnsTransferRequest}
                                                        isCurrentOffer={!isClientOffer}
                                                        required={required}/>
                            <ControlledOfferedHourInput field={hoursFields.mid} label="Mid-level Hours"
                                                        disabled={shouldDisabled}
                                                        isCurrentOffer={!isClientOffer}
                                                        noMinValidation={isReturnsTransferRequest}
                                                        allowZero={isReturnsTransferRequest}
                                                        required={required}/>
                            <ControlledOfferedHourInput field={hoursFields.high} label="High-level Hours"
                                                        disabled={shouldDisabled}
                                                        isCurrentOffer={!isClientOffer}
                                                        noMinValidation={isReturnsTransferRequest}
                                                        allowZero={isReturnsTransferRequest}
                                                        required={required}/>
                            <ControlledOfferedHourInput field={hoursFields.group} label="Group Care Hours"
                                                        disabled={shouldDisabled}
                                                        isCurrentOffer={!isClientOffer}
                                                        noMinValidation={isReturnsTransferRequest}
                                                        allowZero={isReturnsTransferRequest}
                                                        required={required}/>
                        </>
                    }
                </div>
            </div>
        </div>
    );
}
